import { useHistory, useLocation } from 'react-router-dom'
import { LAST_VIEWED_WEEK_KEY } from './utils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AlmanacFileCalendarEvent } from '../types'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { CalendarState } from '@react-stately/calendar'
import { CalendarDate, parseDate } from '@internationalized/date'
import { useModals } from 'almanac-ui/Modal'
import { FullScreenPreviewWithCarouselV2 } from '../FullScreenPreview/FullScreenPreviewWithCarouselV2'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { reportError } from 'utils/error'

dayjs.extend(isSameOrAfter)

export const LAST_OPENED_POST_DATE_KEY = 'lastOpenedPostDate'
export const DATE_PARAM_KEY = 'date'
export const FULLSCREEN_POST_ID_KEY = 'fullscreenPostId'
export const FULLSCREEN_POST_KEY = 'fullscreenPost'

interface Props {
  projectKey?: string
  eventsData?: AlmanacFileCalendarEvent[]
  isLoading?: boolean
  weekDaysInView?: (CalendarDate | undefined)[]
  state?: CalendarState
  useToday?: boolean
  daysInWeek?: number
  dateParam?: string | null
  onDateParamChange?: (date: string | null) => void
  isDateParamSet?: boolean
  onIsDateParamSetChange?: (isDateParamSet: boolean) => void
  offset?: number
  setOffset?: (offset: number) => void
}

export const useOpenLastFullscreenPost = ({
  projectKey,
  eventsData = [],
  isLoading,
  weekDaysInView = [],
  state,
  dateParam,
  onDateParamChange,
  isDateParamSet,
  onIsDateParamSetChange,
  useToday,
  daysInWeek,
  offset,
  setOffset,
}: Props) => {
  const [customDateParamSet, setCustomDateParamSet] = useState(false)
  const { openModal, closeModal } = useModals()
  const dateParamRef = useRef(false)
  const navigationRef = useRef(false)
  const dateOffsetRef = useRef(false)
  const fullscreenPostRef = useRef(false)

  const { onOpenFullscreenPost } = useOpenFullscreenPost({})

  const history = useHistory()
  const { search } = useLocation()

  const { currentFolder } = useCurrentFolderStore()
  const currentFolderStartDate = currentFolder?.brand_plan_schedule?.start_date

  const firstEventFromToday = eventsData
    .sort((a, b) => dayjs(a.post_at).diff(dayjs(b.post_at)))
    .filter((event) => dayjs(event.post_at).startOf('day').isSameOrAfter(dayjs().startOf('day')))[0]

  // set the current date with the following priority:
  // 1. explicit date URL param (e.g. ?date=2024-01-15) - handled first
  // 2. the post date of the last opened post from the url, if there is one
  // 3. the post date of the first event starting from today, if there is one
  // 4. the last viewed week from local storage
  // 5. the start date of the current folder's schedule, if there is one (overrides 2-4)
  useEffect(() => {
    if (!state || !onDateParamChange || !onIsDateParamSetChange || !!customDateParamSet) return

    const params = new URLSearchParams(search)

    // Handle explicit date URL param
    const dateFromUrl = params.get(DATE_PARAM_KEY)
    if (dateFromUrl) {
      onDateParamChange(dateFromUrl)
      try {
        state.setFocusedDate(parseDate(dateFromUrl))
      } catch (error) {
        reportError(error, { severity: 'error', metaData: { dateFromUrl } })
      }
      setTimeout(() => {
        onIsDateParamSetChange(true)
        setCustomDateParamSet(true)

        params.delete(DATE_PARAM_KEY)
        history.replace({ search: params.toString() })
      }, 1000)

      return
    }

    const firstEventFromTodayDate = firstEventFromToday?.post_at
      ? dayjs(firstEventFromToday.post_at).format('YYYY-MM-DD')
      : null

    const currentFolderStartDateParsed = currentFolderStartDate
      ? dayjs(currentFolderStartDate).format('YYYY-MM-DD')
      : null

    const lastOpenedPostDate = params.get(LAST_OPENED_POST_DATE_KEY)
    const lastViewedWeek = localStorage.getItem(`${LAST_VIEWED_WEEK_KEY}_${projectKey || 'workspace'}`)

    let dateParamValue = lastOpenedPostDate || firstEventFromTodayDate || lastViewedWeek
    if (!!projectKey) dateParamValue = currentFolderStartDateParsed

    params.delete(LAST_OPENED_POST_DATE_KEY)

    // Need a 1 second delay to ensure the data is loaded in the calendar before setting the date param set to true and showing the calendar.
    // This is to avoid the calendar from showing a different date than the one the user is taken to on load.
    setTimeout(() => {
      onIsDateParamSetChange(true)
    }, 1000)

    if (
      (isLoading && !firstEventFromToday?.post_at) ||
      navigationRef.current ||
      (dateParamRef.current && dateParamValue === dateParam)
    )
      return

    dateParamRef.current = true

    // If the last opened post date is in the current week, return
    const daysInView = weekDaysInView.map((day) => dayjs(day?.toDate(state.timeZone)).format('YYYY-MM-DD'))
    if (daysInView.includes(lastOpenedPostDate || '')) {
      return
    }
    onDateParamChange(dateParamValue)

    if (dateParamValue) {
      try {
        state.setFocusedDate(parseDate(dateParamValue))
      } catch (error) {
        reportError(error, { severity: 'error', metaData: { dateParamValue } })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    firstEventFromToday?.post_at,
    currentFolderStartDate,
    projectKey,
    search,
    isLoading,
    state,
    dateParam,
    currentFolder?.brand_plan_schedule?.id,
  ])

  // Calculate the offset to ensure:
  // 1. the value of the date param is always the first day of the week, or
  // 2. 'today' is always the first day of the week if the user has clicked on 'Today'
  useEffect(() => {
    if (!state || !daysInWeek || !setOffset) return

    const day = useToday ? dayjs() : dayjs(dateParam || dayjs())
    const diff = day.diff(dayjs(state.visibleRange.start.toDate(state.timeZone)), 'day')
    const calcOffset = diff < 0 ? 0 : diff % daysInWeek

    // If the offset is the same as the current offset and has already been set, return
    if (navigationRef.current || (dateOffsetRef.current && calcOffset === offset)) return

    setOffset(calcOffset)
    if (dateParam) dateOffsetRef.current = true
  }, [daysInWeek, state?.visibleRange.start, currentFolder, dateParam, useToday, offset, setOffset]) // eslint-disable-line react-hooks/exhaustive-deps

  const resetLastOpenedPostDate = () => {
    const queryParams = new URLSearchParams(search)
    navigationRef.current = true
    queryParams.delete(LAST_OPENED_POST_DATE_KEY)
    history.replace({ search: queryParams.toString() })
  }

  const resetNavigation = () => {
    navigationRef.current = false
  }

  const openFullscreenPost = useCallback(
    (eventKey: string, eventPostAt: string, eventChannel: ChannelType | DesignChannel) => {
      openModal(FullScreenPreviewWithCarouselV2, {
        id: eventKey,
        channel: eventChannel,
        showCarousel: true,
        filters: {},
        onClose: closeModal,
        startDate: eventPostAt,
      })

      onOpenFullscreenPost(eventKey, eventPostAt, eventChannel)
    },
    [openModal, closeModal, onOpenFullscreenPost],
  )

  useEffect(() => {
    const queryParams = new URLSearchParams(search)
    if (fullscreenPostRef.current) return

    try {
      const event = JSON.parse(queryParams.get(FULLSCREEN_POST_KEY) || '{}')
      if (event.id) {
        fullscreenPostRef.current = true
        openFullscreenPost(event.id, event.post_at, event.channel)
      }
    } catch (error) {
      reportError(error)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isDateParamSet,
    dateParam,
    resetLastOpenedPostDate,
    resetNavigation,
    openFullscreenPost,
    onOpenFullscreenPost,
  }
}

export const useOpenFullscreenPost = ({
  fileKey,
  filePostAt,
  fileChannel,
}: {
  fileKey?: string
  filePostAt?: string
  fileChannel?: ChannelType | DesignChannel | null
}) => {
  const { search } = useLocation()
  const history = useHistory()
  const location = useLocation()

  const isDesignPage = location.pathname.startsWith('/designs/')
  const isArticlePage = location.pathname.startsWith('/articles/')

  const setLastOpenedPostDate = (eventId: string) => {
    const queryParams = new URLSearchParams(search)
    const queryFullscreenId = queryParams.get(FULLSCREEN_POST_ID_KEY)

    if (queryFullscreenId === eventId || !eventId) return

    queryParams.set(FULLSCREEN_POST_ID_KEY, eventId)
    history.replace({ search: queryParams.toString() })
  }

  const onOpenFullscreenPost = (eventKey: string, eventPostAt: string, eventChannel: ChannelType | DesignChannel) => {
    const queryParams = new URLSearchParams(search)

    const event = JSON.parse(queryParams.get(FULLSCREEN_POST_KEY) || '{}')
    if (event && event.id === eventKey) return

    queryParams.set(
      FULLSCREEN_POST_KEY,
      JSON.stringify({
        id: eventKey,
        post_at: eventPostAt,
        channel: eventChannel,
      }),
    )
    history.push({ search: queryParams.toString() })
  }

  // Open the fullscreen post if the file key, post at, and channel are provided
  // and the current page is not a design or article page
  useEffect(() => {
    if (!fileKey || !filePostAt || !fileChannel || isDesignPage || isArticlePage) return

    setLastOpenedPostDate(fileKey)
    onOpenFullscreenPost(fileKey, filePostAt, fileChannel)
  }, [fileKey, isDesignPage, isArticlePage]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    setLastOpenedPostDate,
    onOpenFullscreenPost,
  }
}
